// Modal para aceptar una cotización sin OC formal.
// El cliente confirmó por WhatsApp/teléfono/email y mandará la OC después.
// Se guarda la confirmación + datos de facilitación parciales en la cotización.

import React, { useState, useEffect } from 'react';
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Button, Form, FormGroup, Label, Input, Row, Col, Alert, Card, CardBody,
} from 'reactstrap';
import { quotesAPI } from '../../services/api';
import api from '../../services/api';

const AcceptQuoteModal = ({ isOpen, toggle, quote, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [facilitators, setFacilitators] = useState([]);

  const [formData, setFormData] = useState({
    confirmation_method: 'whatsapp',
    confirmation_date: new Date().toISOString().split('T')[0],
    confirmation_reference: '',
    // Facilitación (todo opcional)
    facilitator_id: '',
    service_delivery_date: '',
    location: '',
    workshop_name: '',
    session_schedule: '',
    total_hours: '',
    entry_requirements: '',
    program_required: false,
    diplomas_required: false,
    credit_days: '',
    notes: '',
    client_need: '',
    sessions: '',
    participants: '',
    groups: '',
    language: '',
    entry_contact_name: '',
    entry_contact_phone: '',
  });

  useEffect(() => {
    if (isOpen) {
      setError(null);
      setFormData(prev => ({
        ...prev,
        confirmation_date: new Date().toISOString().split('T')[0],
      }));
      loadFacilitators();
    }
  }, [isOpen]);

  const loadFacilitators = async () => {
    try {
      const response = await api.get('/facilitators/', { params: { is_active: true } });
      setFacilitators(response.data.results || response.data);
    } catch {
      // no crítico
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async () => {
    setError(null);
    if (!formData.confirmation_method) {
      setError('Selecciona el medio de confirmación');
      return;
    }
    if (!formData.confirmation_date) {
      setError('Ingresa la fecha de confirmación');
      return;
    }

    setLoading(true);
    try {
      // Solo enviar campos con valor para no contaminar el JSON guardado
      const payload = { confirmation_method: formData.confirmation_method, confirmation_date: formData.confirmation_date };
      if (formData.confirmation_reference) payload.confirmation_reference = formData.confirmation_reference;
      if (formData.facilitator_id) payload.facilitator_id = parseInt(formData.facilitator_id);
      if (formData.service_delivery_date) payload.service_delivery_date = formData.service_delivery_date;
      if (formData.location) payload.location = formData.location;
      if (formData.workshop_name) payload.workshop_name = formData.workshop_name;
      if (formData.session_schedule) payload.session_schedule = formData.session_schedule;
      if (formData.total_hours) payload.total_hours = parseFloat(formData.total_hours);
      if (formData.entry_requirements) payload.entry_requirements = formData.entry_requirements;
      if (formData.credit_days) payload.credit_days = parseInt(formData.credit_days);
      if (formData.notes) payload.notes = formData.notes;
      if (formData.client_need) payload.client_need = formData.client_need;
      if (formData.sessions) payload.sessions = parseInt(formData.sessions);
      if (formData.participants) payload.participants = parseInt(formData.participants);
      if (formData.groups) payload.groups = parseInt(formData.groups);
      if (formData.language) payload.language = formData.language;
      if (formData.entry_contact_name) payload.entry_contact_name = formData.entry_contact_name;
      if (formData.entry_contact_phone) payload.entry_contact_phone = formData.entry_contact_phone;
      payload.program_required = formData.program_required;
      payload.diplomas_required = formData.diplomas_required;

      const response = await quotesAPI.acceptManually(quote.id, payload);
      toggle();
      if (onSuccess) onSuccess(response.data.quote);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Error al aceptar la propuesta';
      setError(typeof msg === 'object' ? JSON.stringify(msg) : msg);
    } finally {
      setLoading(false);
    }
  };

  if (!quote) return null;

  const confirmationMethods = [
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'email', label: 'Correo Electrónico' },
    { value: 'phone', label: 'Llamada Telefónica' },
    { value: 'verbal', label: 'Verbal / Presencial' },
    { value: 'other', label: 'Otro' },
  ];

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" backdrop="static" className="modal-dialog-centered">
      <ModalHeader toggle={toggle} className="bg-gradient-success border-0">
        <div>
          <h5 className="mb-0 text-white font-weight-bold">
            <i className="ni ni-check-bold me-2"></i>
            Aceptar Propuesta sin OC
          </h5>
          <small className="text-white opacity-8 d-block mt-1">
            El cliente confirmó — la OC formal llegará después
          </small>
        </div>
      </ModalHeader>

      <ModalBody className="p-4">
        {error && (
          <Alert color="danger" className="shadow-sm">
            <span className="alert-icon"><i className="ni ni-support-16"></i></span>
            <span className="alert-text">{error}</span>
          </Alert>
        )}

        {/* Resumen de la cotización */}
        <Card className="shadow-sm border-0 mb-4 bg-gradient-secondary">
          <CardBody className="p-3">
            <Row>
              <Col md="6">
                <p className="text-xs text-white text-uppercase mb-1">Propuesta</p>
                <h6 className="text-white mb-0">{quote.proposal_number} v{quote.version}</h6>
              </Col>
              <Col md="6">
                <p className="text-xs text-white text-uppercase mb-1">Cliente</p>
                <h6 className="text-white mb-0">{quote.client_name || 'N/A'}</h6>
              </Col>
            </Row>
            <hr className="horizontal light my-2" />
            <Row>
              <Col md="6">
                <p className="text-xs text-white text-uppercase mb-1">Monto</p>
                <h5 className="text-white mb-0">
                  ${parseFloat(quote.subtotal || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  <small className="text-white opacity-8 ms-2">+ IVA</small>
                </h5>
              </Col>
              <Col md="6">
                <p className="text-xs text-white text-uppercase mb-1">Servicio</p>
                <p className="text-white text-sm mb-0">{quote.service_type || quote.category || 'N/A'}</p>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Alert color="info" className="shadow-sm mb-4">
          <span className="alert-icon"><i className="ni ni-bell-55"></i></span>
          <span className="alert-text">
            Esta propuesta quedará <strong>Aceptada</strong>. Cuando llegue la OC, se sube desde el Inbox y se vincula automáticamente.
            Los datos de facilitación que llenes aquí se pre-cargarán en ese momento.
          </span>
        </Alert>

        <Form>
          {/* Sección 1: Confirmación */}
          <div className="card shadow-sm border-0 mb-3">
            <div className="card-header bg-light py-2">
              <h6 className="mb-0 text-dark">
                <i className="ni ni-check-bold me-2 text-success"></i>
                Confirmación del Cliente <span className="text-danger">*</span>
              </h6>
            </div>
            <div className="card-body p-3">
              <Row>
                <Col md="6">
                  <FormGroup className="mb-3">
                    <Label className="form-control-label text-uppercase text-xs font-weight-bold">
                      Medio de Confirmación <span className="text-danger">*</span>
                    </Label>
                    <Input type="select" name="confirmation_method" className="form-control-alternative"
                      value={formData.confirmation_method} onChange={handleChange}>
                      {confirmationMethods.map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup className="mb-3">
                    <Label className="form-control-label text-uppercase text-xs font-weight-bold">
                      Fecha de Confirmación <span className="text-danger">*</span>
                    </Label>
                    <Input type="date" name="confirmation_date" className="form-control-alternative"
                      value={formData.confirmation_date} onChange={handleChange} />
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup className="mb-0">
                <Label className="form-control-label text-uppercase text-xs font-weight-bold">
                  Referencia <small className="text-muted text-lowercase font-weight-normal">(opcional)</small>
                </Label>
                <Input type="textarea" name="confirmation_reference" className="form-control-alternative"
                  value={formData.confirmation_reference} onChange={handleChange}
                  placeholder="Ej: WhatsApp de Ana García confirmando el taller del 10 de mayo" rows="2" />
              </FormGroup>
            </div>
          </div>

          {/* Sección 2: Facilitación (todo opcional) */}
          <div className="card shadow-sm border-0 mb-3">
            <div className="card-header bg-light py-2">
              <h6 className="mb-0 text-dark">
                <i className="ni ni-calendar-grid-58 me-2 text-primary"></i>
                Datos de Facilitación{' '}
                <small className="text-muted font-weight-normal">(opcionales — se completarán cuando llegue la OC)</small>
              </h6>
            </div>
            <div className="card-body p-3">
              <Row>
                <Col md="6">
                  <FormGroup className="mb-3">
                    <Label className="form-control-label text-uppercase text-xs font-weight-bold">Facilitador</Label>
                    <Input type="select" name="facilitator_id" className="form-control-alternative"
                      value={formData.facilitator_id} onChange={handleChange}>
                      <option value="">— Sin asignar —</option>
                      {facilitators.map(f => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup className="mb-3">
                    <Label className="form-control-label text-uppercase text-xs font-weight-bold">Fecha de Servicio</Label>
                    <Input type="date" name="service_delivery_date" className="form-control-alternative"
                      value={formData.service_delivery_date} onChange={handleChange} />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="6">
                  <FormGroup className="mb-3">
                    <Label className="form-control-label text-uppercase text-xs font-weight-bold">Nombre del Taller</Label>
                    <Input type="text" name="workshop_name" className="form-control-alternative"
                      value={formData.workshop_name} onChange={handleChange} placeholder="Ej: Liderazgo Situacional" />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup className="mb-3">
                    <Label className="form-control-label text-uppercase text-xs font-weight-bold">Horario</Label>
                    <Input type="text" name="session_schedule" className="form-control-alternative"
                      value={formData.session_schedule} onChange={handleChange} placeholder="Ej: 09:00 - 13:00" />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="6">
                  <FormGroup className="mb-3">
                    <Label className="form-control-label text-uppercase text-xs font-weight-bold">Lugar</Label>
                    <Input type="text" name="location" className="form-control-alternative"
                      value={formData.location} onChange={handleChange} placeholder="Ciudad, instalación..." />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup className="mb-3">
                    <Label className="form-control-label text-uppercase text-xs font-weight-bold">Total de Horas</Label>
                    <Input type="number" name="total_hours" className="form-control-alternative"
                      value={formData.total_hours} onChange={handleChange} placeholder="Ej: 8" min="0" />
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup className="mb-3">
                <Label className="form-control-label text-uppercase text-xs font-weight-bold">Necesidad del Cliente</Label>
                <Input type="textarea" name="client_need" className="form-control-alternative"
                  value={formData.client_need} onChange={handleChange}
                  placeholder="Ej: Entrenar a los aprendices en Strengths Finder" rows="2" />
              </FormGroup>
              <FormGroup className="mb-3">
                <Label className="form-control-label text-uppercase text-xs font-weight-bold">Requisitos de Ingreso</Label>
                <Input type="textarea" name="entry_requirements" className="form-control-alternative"
                  value={formData.entry_requirements} onChange={handleChange}
                  placeholder="Ej: Zapato industrial, casco, no celulares" rows="2" />
              </FormGroup>
              <Row>
                <Col md="6">
                  <FormGroup className="mb-3">
                    <Label className="form-control-label text-uppercase text-xs font-weight-bold">Contacto de Entrada</Label>
                    <Input type="text" name="entry_contact_name" className="form-control-alternative"
                      value={formData.entry_contact_name} onChange={handleChange}
                      placeholder="Nombre de quien da acceso al lugar" />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup className="mb-3">
                    <Label className="form-control-label text-uppercase text-xs font-weight-bold">Teléfono del Contacto</Label>
                    <Input type="text" name="entry_contact_phone" className="form-control-alternative"
                      value={formData.entry_contact_phone} onChange={handleChange}
                      placeholder="Ej: 444 123 4567" />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="3">
                  <FormGroup className="mb-3">
                    <Label className="form-control-label text-uppercase text-xs font-weight-bold">Sesiones</Label>
                    <Input type="number" name="sessions" className="form-control-alternative"
                      value={formData.sessions} onChange={handleChange} placeholder="Ej: 6" min="1" />
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup className="mb-3">
                    <Label className="form-control-label text-uppercase text-xs font-weight-bold">Personas</Label>
                    <Input type="number" name="participants" className="form-control-alternative"
                      value={formData.participants} onChange={handleChange} placeholder="Ej: 48" min="1" />
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup className="mb-3">
                    <Label className="form-control-label text-uppercase text-xs font-weight-bold">Grupos</Label>
                    <Input type="number" name="groups" className="form-control-alternative"
                      value={formData.groups} onChange={handleChange} placeholder="Ej: 3" min="1" />
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup className="mb-3">
                    <Label className="form-control-label text-uppercase text-xs font-weight-bold">Idioma</Label>
                    <Input type="text" name="language" className="form-control-alternative"
                      value={formData.language} onChange={handleChange} placeholder="Español" />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="4">
                  <FormGroup check className="mb-3">
                    <Input type="checkbox" id="program_required" name="program_required"
                      checked={formData.program_required} onChange={handleChange} />
                    <Label check for="program_required" className="text-dark">Programa requerido</Label>
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup check className="mb-3">
                    <Input type="checkbox" id="diplomas_required" name="diplomas_required"
                      checked={formData.diplomas_required} onChange={handleChange} />
                    <Label check for="diplomas_required" className="text-dark">Diplomas requeridos</Label>
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup className="mb-0">
                    <Label className="form-control-label text-uppercase text-xs font-weight-bold">Días de Crédito</Label>
                    <Input type="number" name="credit_days" className="form-control-alternative"
                      value={formData.credit_days} onChange={handleChange} placeholder="30" min="0" />
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup className="mb-0">
                <Label className="form-control-label text-uppercase text-xs font-weight-bold">Notas</Label>
                <Input type="textarea" name="notes" className="form-control-alternative"
                  value={formData.notes} onChange={handleChange} rows="2" />
              </FormGroup>
            </div>
          </div>
        </Form>
      </ModalBody>

      <ModalFooter className="border-0 bg-light">
        <Button color="secondary" onClick={toggle} disabled={loading}>
          <i className="ni ni-fat-remove me-2"></i>Cancelar
        </Button>
        <Button color="success" onClick={handleSubmit} disabled={loading}>
          {loading ? (
            <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Guardando...</>
          ) : (
            <><i className="ni ni-check-bold me-2"></i>Aceptar Propuesta</>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AcceptQuoteModal;
